/**
 * "Who sounds?" — timbre discrimination + vocabulary with a narrator.
 * The MVP pool uses instruments/pads (existing assets); animals enter
 * post-MVP by changing only the pool.
 */

import { createSignal, For, onCleanup, onMount, Show } from "solid-js";
import {
  createProgressStore,
  createWhoSoundsGame,
  gameStickerId,
  type NarrationKey,
  type WhoSoundsItem,
} from "@melodimals/game-core";
import { Star, Volume2 } from "@fleet/icons";
import { cn } from "@/lib/cn";
import { useAudio } from "@/providers/audio-provider";
import { SoundLoadError } from "@/features/instruments/sound-load-error";
import { RewardPopup } from "@/features/rewards/reward-popup";
import { useRewards } from "@/features/rewards/use-rewards";
import { useAppConfig } from "@/providers/config-provider";

const POOL: WhoSoundsItem<NarrationKey>[] = [
  { id: "instrument.piano", icon: "🎹", sound: { instrument: "piano", key: "C4" } },
  { id: "instrument.xylophone", icon: "🌈", sound: { instrument: "xylophone", key: "C5" } },
  { id: "instrument.drums", icon: "🥁", sound: { instrument: "drums", key: "kick" } },
  { id: "sound.bell", icon: "🔔", sound: { instrument: "drums", key: "cowbell" } },
  { id: "sound.wood", icon: "🪵", sound: { instrument: "drums", key: "woodblock" } },
  { id: "sound.magic", icon: "✨", sound: { instrument: "drums", key: "steel" } },
];

export function WhoSoundsGame() {
  const audio = useAudio();
  const progress = createProgressStore();
  const rewards = useRewards();
  // Rounds and options come from configuration (adult preset or env).
  const { games } = useAppConfig().config();
  const game = createWhoSoundsGame({
    pool: POOL,
    choices: games.whoSoundsChoices,
    rounds: games.whoSoundsRounds,
  });

  const [round, setRound] = createSignal(game.currentRound());
  const [roundNumber, setRoundNumber] = createSignal(1);
  const [finished, setFinished] = createSignal(false);
  const [ready, setReady] = createSignal(false);
  // P23/I3: without this state, a failure to fetch the samples left the game
  // rendered with all three options DISABLED forever and no message.
  const [soundFailed, setSoundFailed] = createSignal(false);
  const timers: ReturnType<typeof setTimeout>[] = [];
  onCleanup(() => {
    for (const t of timers) {
      clearTimeout(t);
    }
  });

  const playTarget = () => {
    const { sound } = round().target;
    audio.playNote(sound.instrument, sound.key);
  };

  const preloadSounds = () => {
    setSoundFailed(false);
    void (async () => {
      try {
        await Promise.all([
          audio.loadInstrument("piano"),
          audio.loadInstrument("xylophone"),
          audio.loadInstrument("drums"),
        ]);
        setReady(true);
        void audio.narrator.say("guide.listen");
        timers.push(setTimeout(playTarget, 1000));
      } catch {
        setSoundFailed(true);
      }
    })();
  };

  onMount(preloadSounds);

  const choose = (item: WhoSoundsItem<NarrationKey>) => {
    const result = game.choose(item.id);
    if (result === "miss") {
      void audio.narrator.say("guide.listen");
      timers.push(setTimeout(playTarget, 900));
      return;
    }
    // Correct answer: its sound plays and the narrator names it (vocabulary).
    audio.playNote(item.sound.instrument, item.sound.key);
    void audio.narrator.say(item.id);
    if (result === "finished") {
      setFinished(true);
      progress.recordLevel("who-sounds", game.state().firstTryHits);
      rewards.award(gameStickerId("who-sounds"));
      timers.push(
        setTimeout(() => {
          void audio.narrator.say("praise.well-done");
        }, 1100),
      );
      return;
    }
    setRound(game.currentRound());
    setRoundNumber(game.state().round);
    timers.push(setTimeout(playTarget, 1200));
  };

  return (
    <div class="flex h-full flex-col items-center justify-center gap-[clamp(0.75rem,3vh,2rem)]">
      {/* P23/I3: el error ENVUELVE al juego, no se encadena. */}
      <Show when={soundFailed()}>
        <SoundLoadError testId="who-sounds" onRetry={preloadSounds} />
      </Show>
      <Show when={!soundFailed()}>
        <Show
          when={!finished()}
          fallback={
            <div class="flex items-center gap-2" aria-hidden="true">
              <Star class="h-10 w-10 fill-amber-400 text-amber-500" />
              <Star class="h-14 w-14 fill-amber-400 text-amber-500" />
              <Star class="h-10 w-10 fill-amber-400 text-amber-500" />
            </div>
          }
        >
          <button
            type="button"
            onPointerDown={playTarget}
            disabled={!ready()}
            class="flex aspect-square w-[clamp(4.5rem,18vmin,7rem)] items-center justify-center rounded-full bg-white shadow-lg transition-transform active:scale-90 disabled:opacity-50"
            aria-label="play sound"
          >
            <Volume2 class="h-1/2 w-1/2 text-cyan-700" />
          </button>

          <div class="flex gap-[clamp(0.75rem,3vmin,1.25rem)]">
            <For each={round().options}>
              {(item) => (
                <button
                  type="button"
                  onPointerDown={() => {
                    choose(item);
                  }}
                  disabled={!ready()}
                  class={cn(
                    "flex aspect-square w-[clamp(5rem,22vmin,8rem)] items-center justify-center rounded-3xl bg-white text-[clamp(2.5rem,11vmin,3.75rem)] shadow-md",
                    "transition-transform hover:scale-105 active:scale-90 disabled:opacity-50",
                  )}
                  aria-label={item.id}
                >
                  <span aria-hidden="true">{item.icon}</span>
                </button>
              )}
            </For>
          </div>

          <div class="flex gap-1" aria-hidden="true">
            <For each={Array.from({ length: games.whoSoundsRounds })}>
              {(_, i) => (
                <span
                  class={`h-3 w-3 rounded-full ${i() < roundNumber() - 1 ? "bg-cyan-600" : "bg-white/60"}`}
                />
              )}
            </For>
          </div>
        </Show>
      </Show>

      <RewardPopup stickerId={rewards.pending()} onDismiss={rewards.dismiss} />
    </div>
  );
}
